import { useEffect, useMemo } from 'react';

import { Link, RichTextEditor } from '@mantine/tiptap';
import '@mantine/tiptap/styles.css';
import { Color } from '@tiptap/extension-color';
import HardBreak from '@tiptap/extension-hard-break';
import Highlight from '@tiptap/extension-highlight';
import Placeholder from '@tiptap/extension-placeholder';
import Subscript from '@tiptap/extension-subscript';
import Superscript from '@tiptap/extension-superscript';
import TextAlign from '@tiptap/extension-text-align';
import TextStyle from '@tiptap/extension-text-style';
import Underline from '@tiptap/extension-underline';
import { Extensions, useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import { ResizableImage } from 'tiptap-extension-resizable-image';
import 'tiptap-extension-resizable-image/styles.css';
import { Input, cx } from 'ui';

import { EditorControlGroups } from './controlGroupVariations';
import classes from './editor.module.css';
import { RTEInputWrapperProps, RTEProps } from './editor.types';

const EditorWrapper = ({ children, ...rest }: RTEInputWrapperProps) => {
  if (rest) {
    return (
      <Input.Wrapper styles={{ error: { marginTop: rest.error ? 4 : 0 } }} {...rest}>
        {children}
      </Input.Wrapper>
    );
  } else {
    return <>{children}</>;
  }
};

const LinkNoRelativeLinks = Link.extend({
  addAttributes(this) {
    return {
      href: {
        renderHTML: (attributes) => {
          const href = attributes.href;
          // Test if the link contains a protocol, suppose any is fine.
          const isValidLink = /^https?:\/\//.test(href);
          const validLink = isValidLink ? href : `https://${href}`;
          return {
            href: validLink,
          };
        },
      },
    };
  },
});

const HardBreakOnEnter = HardBreak.extend({
  addKeyboardShortcuts() {
    return {
      Enter: () => {
        // If editing an ordered or bullet list, create a new paragraph
        // This allows a double enter to exit the list creation
        if (this.editor.isActive('orderedList') || this.editor.isActive('bulletList')) {
          return this.editor.chain().createParagraphNear().run();
        }
        // Otherwise, continue creating a new paragraph
        return this.editor.chain().createParagraphNear().run();
      },
    };
  },
});

const Editor = ({
  onChange,
  onChangeText,
  value,
  variant = 'full',
  placeHolder,
  inputProps,
  className,
  noRelativeLinks = true,
  disabled = false,
  readOnly,
  textFormattingControlGroupVariant,
  onUpload,
  uploadFn,
  ...props
}: RTEProps) => {
  const extensions = useMemo(() => {
    const _extensions = [
      StarterKit,
      HardBreak,
      Highlight,
      Underline,
      TextAlign.configure({ types: ['heading', 'paragraph'] }),
      Superscript,
      Subscript,
      Placeholder.configure({ placeholder: placeHolder }),
      Link,
      HardBreakOnEnter,
      TextStyle, // required for Color
      Color,
      ResizableImage,
    ];

    if (noRelativeLinks) {
      _extensions.push(LinkNoRelativeLinks);
    }

    return _extensions as Extensions;
  }, [noRelativeLinks, placeHolder]);

  const editor = useEditor({
    extensions: extensions,
    content: value,
    onUpdate: ({ editor }) => {
      onChange?.(editor.getHTML());
      onChangeText?.(editor.getText());
    },
    editable: false,
    parseOptions: {
      preserveWhitespace: true,
    },
  });

  useEffect(() => {
    // Align the possible outside introduced value changes
    if (value !== editor?.getHTML()) {
      editor?.commands?.setContent(value || '<p></p>');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only want to react to value changes, editor is most likely referentially unstable
  }, [value]);

  useEffect(() => {
    editor?.setEditable(!readOnly && !disabled);
  }, [editor, readOnly, disabled]);

  return (
    <EditorWrapper {...inputProps}>
      <RichTextEditor
        editor={editor}
        className={cx(classes.editor, className)}
        classNames={{
          content: classes.Editor__content,
        }}
        mod={{
          disabled: disabled,
          'read-only': readOnly,
        }}
        {...props}
      >
        <EditorControlGroups
          variant={variant}
          textFormattingControlGroupVariant={textFormattingControlGroupVariant}
          editor={editor}
          onUpload={onUpload}
          uploadFn={uploadFn}
        />
        <RichTextEditor.Content
          style={{ cursor: disabled ? 'default' : 'text' }}
          // styles applied to the <RichTextEditor.Content> are actually applied to the .mantine-RichTextEditor-typographyStylesProvider level
          className={cx(classes.content, className)}
        />
      </RichTextEditor>
    </EditorWrapper>
  );
};

export { Editor };
